/**
 * A dynamic-import / chunk-load failure: the app requested a JS or CSS chunk the
 * server no longer has (a stale index.html still points at old chunk hashes after
 * a deploy), a `<link rel="modulepreload">` failed, or the network dropped
 * mid-request. Each engine phrases the same failure differently, so recovery code
 * that matched only one phrasing left the others white-screening. This is the
 * single source of truth for every recovery site — the router-level
 * RootErrorBoundary, the inner component-level ErrorBoundary (which wraps the
 * designer/dashboard — the primary paid surface), and the global handlers in
 * main.tsx — so the matchers can't drift apart again.
 *
 *   - Chrome:    "Failed to fetch dynamically imported module"
 *   - Firefox:   "error loading dynamically imported module"
 *   - Safari:    "Importing a module script failed"
 *   - stale export requested from a new chunk: "Importing binding name '…' is not found"
 *   - webpack-style: "ChunkLoadError" / "Loading chunk N failed"
 *   - preloaded CSS: "Unable to preload CSS"
 */
export const CHUNK_LOAD_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Importing binding name|ChunkLoadError|Loading chunk|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  const msg =
    error instanceof Error
      ? error.message
      : String((error as { message?: string } | null | undefined)?.message ?? error ?? '');
  return CHUNK_LOAD_RE.test(msg);
}
